<template>
    <div class="auth-modal" v-show="isVisible">
        <div class="login-form-container">
            <h2>Sign In</h2>
            
            <form 
                ref="loginForm"
                @submit.prevent="handleSubmit" 
                autocomplete="on"
            >
                <div class="form-group">
                    <label for="login-email">Email:</label>
                    <input
                        ref="emailInput"
                        v-model="email"
                        type="email"
                        name="username"
                        autocomplete="username"
                        required
                    />
                </div>
                <div class="form-group">
                    <label for="login-password">Password:</label>
                    <div class="password-wrapper">
                        <input
                            ref="passwordInput"
                            id="login-password"
                            name="password"
                            :type="showPassword ? 'text' : 'password'"
                            v-model="password"
                            autocomplete="current-password"
                            required
                        />
                        <button
                            type="button"
                            class="toggle-eye"
                            @click="showPassword = !showPassword"
                            tabindex="-1"
                        >
                            {{ showPassword ? '👁️' : '👁️‍🗨️' }}
                        </button>
                    </div>
                </div>
                <div class="form-group remember-group">
                    <label for="remember-me" class="remember-label">Remember Me</label>
                    <input
                        id="remember-me"
                        type="checkbox"
                        v-model="rememberMe"                       
                    />
                </div>

                <div v-if="localError && !showVerificationPrompt" class="error-message">
                    {{ localError }}
                    <div v-if="showForgotPasswordPrompt" class="forgot-password-row">
                        <button type="button" class="user-modal-submit-btn blue" @click="handleForgotPassword">Forgot Password?</button>
                    </div>
                </div>

                <div v-if="showVerificationPrompt" class="error-message">
                    <div>Your account is not confirmed.<br>A verification code has been sent to your email.</div>
                    <input v-model="verificationCode" placeholder="Enter verification code" class="verification-input" />
                    <button type="button" class="user-modal-submit-btn blue" @click="handleConfirmCode" :disabled="isLoggingIn || !verificationCode">
                        Confirm Account
                    </button>
                    <button type="button" class="user-modal-submit-btn" @click="handleResendVerification" :disabled="isLoggingIn">
                        Resend Verification Email
                    </button>
                    <div v-if="verificationError" class="error-message">{{ verificationError }}</div>
                </div>

                <div class="button-row">
                    <button type="submit" class="user-modal-submit-btn green" :disabled="loading">
                        {{ loading ? 'Signing in...' : 'Sign In' }}
                    </button>     
                    <button type="button" class="user-modal-cancel-btn" @click="$emit('cancel')">
                        Cancel
                    </button>
                </div>
                <div class="create-row">
                    <button type="button" class="user-modal-submit-btn blue" @click="$emit('show-register')">
                        Create New Account ...
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import mitt from 'mitt';
    const emitter = mitt();

    export default {
        name: 'LoginModal',
        props: {
            isVisible: {
                type: Boolean,
                default: false
            },
            loading: Boolean,
            error: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                email: '',
                password: '',
                showPassword: false,
                isLoggingIn: false,
                localError: '', 
                rememberMe: false,
                showVerificationPrompt: false,
                showForgotPasswordPrompt: false,
                verificationCode: '',
                verificationError: ''
            };
        },
        watch: {
            error: {
                immediate: true,
                handler(newVal) {
                    this.localError = newVal;
                    if (newVal) {
                        const isNotConfirmed = 
                            newVal.includes('UserNotConfirmedException') || 
                            newVal.toLowerCase().includes('not confirmed');
                        
                        // Show forgot password for ANY credential error
                        const isCredentialError =
                            newVal.toLowerCase().includes('invalid email or password') ||
                            newVal.toLowerCase().includes('no such user') ||
                            newVal.toLowerCase().includes('invalid password');

                        if (isNotConfirmed) {
                            // console.log('Detected unconfirmed user, showing prompt...');
                            this.showVerificationPrompt = true;
                        } else {
                            this.showVerificationPrompt = false;
                        }
                        this.showForgotPasswordPrompt = isCredentialError;
                    } else {
                        this.showVerificationPrompt = false;
                        this.showForgotPasswordPrompt = false;
                    }
                }
            }
        },
        methods: {
            handleKeydown(e) {
                if (e.key === 'Escape' && this.isVisible) {
                    this.$emit('cancel');
                }
            },
            async handleSubmit(event) {
                event.stopPropagation();
                this.localError = '';
                this.verificationError = '';
                if (this.showVerificationPrompt) return;
                this.$emit('submit', {
                    email: this.email,
                    password: this.password,
                    rememberMe: this.rememberMe
                });
            },
			async handleForgotPassword() {
				if (!this.email) {
					this.localError = 'Please enter your email address first.';
					return;
				}
				// Just tell the parent to switch modals
				this.$emit('forgot-password-requested', { email: this.email });
            },
            async handleConfirmCode() {
                this.verificationError = '';
                this.isLoggingIn = true;
                try {
                    await this.$emit('confirm-verification', {
                        email: this.email,
                        code: this.verificationCode,
                        password: this.password
                    });
                } catch (err) {
                    this.verificationError = err.message || 'Verification failed.';
                } finally {
                    this.isLoggingIn = false;
                }
            },
            async handleResendVerification() {
                this.verificationError = '';
                this.isLoggingIn = true;
                try {
                    await this.$emit('resend-verification', { email: this.email });
                    this.verificationError = 'Verification code resent!';
                } catch (err) {
                    this.verificationError = err.message || 'Resend failed.';
                } finally {
                    this.isLoggingIn = false;
                }
            },
            reset() {
                this.email = '';
                this.password = '';
                this.showPassword = false;
                this.localError = '';
                this.rememberMe = false;
                this.showVerificationPrompt = false;
                this.verificationCode = '';
                this.verificationError = '';
            }
        },
        created(){
            this.localError = ''
            window.addEventListener('keydown', this.handleKeydown);
        },
        mounted() {
            emitter.on('createAccount', this.handleCreateAccount);
        },
        beforeUnmount() {
            emitter.off('createAccount', this.handleCreateAccount);
            window.removeEventListener('keydown', this.handleKeydown);
        }
    }
</script>

<style src="@/assets/user-shared.css"></style>
